import { useNavigate } from '@tanstack/react-router';
import { Award, CalendarPlus, ClipboardPen, Copy, FastForward, Pencil, Send, Settings2, XCircle } from 'lucide-react';
import { useState, type MouseEvent, type ReactNode } from 'react';
import {
  TEST_ACTION_LABELS,
  type AssessmentResultRow,
  type CorrectResultInput,
  type JobDetail,
  type RecordResultInput,
} from '@nest-recruit/shared';
import { Avatar } from '../../../components/Avatar';
import { Button } from '../../../components/Button';
import { Card, MetricStrip, type Metric } from '../../../components/Card';
import { Chip } from '../../../components/Chip';
import { DataTable } from '../../../components/data-table/DataTable';
import { RESULT_TONES } from '../../../components/status-tones';
import type { Tone } from '../../../components/Tone';
import type { Column, SortSpec } from '../../../components/data-table/types';
import { Popover } from '../../../components/Popover';
import { SkeletonLines } from '../../../components/Skeleton';
import { EmptyState, ServerErrorState } from '../../../components/system-states/SystemState';
import { anchorFrom, type AnchorRect } from '../../../lib/anchored-panel';
import { formatDate, formatDateTime, formatRelative } from '../../../lib/format';
import { EMPTY_SELECTION, isSelected, type SelectionState } from '../../../lib/selection';
import { useRoom } from '../../../shell/SocketProvider';
import { useCanEdit, useCanView } from '../../../stores/session';
import { CandidatePopup, type CandidatePanelTab } from '../../candidates/components/panel/CandidatePanel';
import { useSelected } from '../../comparison/api/comparison-api';
import { useCvBankAccess } from '../../cv-bank/hooks/use-cv-bank-actions';
import { usePipelineOptions } from '../../pipeline/api/pipeline-api';
import { CONTACT_ATTEMPTS_CHECKED_BY_SERVER, reasonOptions } from '../../pipeline/move-intent';
import { ReasonPicker } from '../../sorting/components/ReasonPicker';
import { errorMessage } from '../../../lib/api-client';
import { useToasts } from '../../../stores/toasts';
import { useCorrectResult, useJobResults, useRecordResult } from '../api/assessments-api';
import { attemptLabel, useResultsActions } from '../hooks/use-results-actions';
import { advanceWarning, canCopyLink, offersCvBank, rejectRequestOf } from '../results-rules';
import { BookSessionPanel } from './BookSessionPanel';
import { CorrectResultPanel, RecordResultPanel } from './RecordResultPanel';
import { SessionEditor } from './SessionEditor';
import styles from './Assessments.module.css';
import { useStageLabels } from '../../../lib/modules';

type Rejecting = { readonly rows: readonly AssessmentResultRow[]; readonly anchor: AnchorRect };

/** §L19: what a fail is rejected for, unless the person picks another. */
const FAILED_TEST_REASON = 'ASM-FAIL';
type Booking = { readonly row: AssessmentResultRow; readonly anchor: AnchorRect };
type RowPanel = { readonly row: AssessmentResultRow; readonly anchor: AnchorRect };
type OpenCandidate = { readonly candidateId: string; readonly tab: CandidatePanelTab };

/**
 * ASM-07 — one job's test results, highest score first (F9.16). A result never moves anyone
 * (R8, DECISIONS-LOG "Start R8" Q1): a pass offers "Move to Interview 1" in one click — or, on a
 * job with no interview round, "Select", the same selection as a last-round pass (ADR 0045) — and
 * a fail offers a reject with "Did not meet assessment threshold" already picked, which a person
 * confirms (#2). A candidate submitting on their phone appears here without a refresh (the job
 * room, ADR 0003).
 */
// Each row's icon tile (ADR 0035): waiting on a marker, passed, or below the pass mark.
function resultTone(row: AssessmentResultRow): Tone | undefined {
  if (row.latest?.hasPendingManualGrading) return RESULT_TONES.marking;
  if (row.latest?.didPass === true) return RESULT_TONES.passed;
  if (row.latest?.didPass === false) return RESULT_TONES.below;
  return undefined;
}

export function ResultsTab({ detail }: { readonly detail: JobDetail }): ReactNode {
  const stageLabels = useStageLabels();
  const jobId = detail.id;
  useRoom(`job:${jobId}`);
  const results = useJobResults(jobId);
  const options = usePipelineOptions();
  const canAct = useCanEdit('application');
  const canSeeSelected = useCanView('stage_decision');
  // ADR 0045: with no interview round the test is the job's last step, so a pass is selected here.
  const isFinalStep = detail.interviewRounds.length === 0;
  const selected = useSelected(jobId, isFinalStep && canSeeSelected);
  const actions = useResultsActions(jobId);
  const navigate = useNavigate();
  const [sort, setSort] = useState<readonly SortSpec[]>([]);
  const [selection, setSelection] = useState<SelectionState>(EMPTY_SELECTION);
  const [rejecting, setRejecting] = useState<Rejecting | null>(null);
  const [booking, setBooking] = useState<Booking | null>(null);
  const [isCreatingSession, setIsCreatingSession] = useState(false);
  const [recording, setRecording] = useState<RowPanel | null>(null);
  const [correcting, setCorrecting] = useState<RowPanel | null>(null);
  const recordResult = useRecordResult(jobId);
  const correctResult = useCorrectResult(jobId);
  const show = useToasts((state) => state.show);
  const mayAddToCvBank = useCvBankAccess().canEdit;
  const [openCandidate, setOpenCandidate] = useState<OpenCandidate | null>(null);

  if (results.isPending) return <SkeletonLines count={8} />;
  if (results.error) return <ServerErrorState onRetry={() => void results.refetch()} />;
  const view = results.data;

  if (!view.isAssessmentOn && view.rows.length === 0) {
    return (
      <EmptyState
        title="This job has no test"
        body="Switch the test on in the job's setup, step 5, to send candidates an online test before the first interview."
        action={
          <Button variant="solid" icon={Settings2} onClick={() => void navigate({ to: '/jobs/$jobId/setup/$section', params: { jobId, section: 'stages' } })}>
            Set up a test
          </Button>
        }
      />
    );
  }

  const inStage = view.rows.filter((row) => row.stage === 'assessment');
  const passed = inStage.filter((row) => row.latest?.didPass === true);
  const below = inStage.filter((row) => row.latest?.didPass === false);
  const count = (predicate: (row: AssessmentResultRow) => boolean): number => view.rows.filter(predicate).length;
  const chosen = view.rows.filter((row) => isSelected(selection, row.applicationId) && row.stage === 'assessment');
  const nextRound = view.firstRoundName ?? stageLabels.interview_1;

  // The mockup's "To mark" opens the marking list while anything waits there (ASM-06 has no job filter).
  function toMarkMetric(waiting: number): Metric {
    const metric = { label: 'To mark', value: String(waiting) };
    return waiting > 0 ? { ...metric, onClick: () => void navigate({ to: '/assessments/marking' }) } : metric;
  }

  function advance(rows: readonly AssessmentResultRow[]): void {
    setSelection(EMPTY_SELECTION);
    void actions.runBulk({ action: 'advance', selection: { kind: 'ids', ids: rows.map((row) => row.applicationId) } }, rows, 'interview_1', advanceWarning(rows));
  }

  // As All candidates: a plain click opens the pop-up over the list, Ctrl/Cmd+click the full profile.
  function openPopup(event: MouseEvent<HTMLAnchorElement>, row: AssessmentResultRow): void {
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    setOpenCandidate({ candidateId: row.candidateId, tab: 'tests' });
  }

  function reject(rows: readonly AssessmentResultRow[], event: MouseEvent<HTMLButtonElement>): void {
    setRejecting({ rows, anchor: anchorFrom(event.currentTarget) });
  }

  // F9.18: the row shows the result at once; a refusal puts it back and says why (§5a rule 1).
  function saveRecorded(row: AssessmentResultRow, input: RecordResultInput, scoresheet: File | null): void {
    setRecording(null);
    recordResult.mutate(
      { row, input, scoresheet },
      { onError: (cause) => show({ tone: 'danger', message: `${row.candidateName}'s result wasn't saved`, detail: errorMessage(cause) }) },
    );
  }

  function saveCorrected(row: AssessmentResultRow, input: CorrectResultInput): void {
    setCorrecting(null);
    if (!row.latest) return;
    correctResult.mutate(
      { applicationId: row.applicationId, latest: row.latest, input },
      { onError: (cause) => show({ tone: 'danger', message: `${row.candidateName}'s score wasn't corrected`, detail: errorMessage(cause) }) },
    );
  }

  function recordButton(row: AssessmentResultRow, variant: 'solid' | 'quiet'): ReactNode {
    return (
      <Button variant={variant} size="sm" icon={ClipboardPen} onClick={(event: MouseEvent<HTMLButtonElement>) => setRecording({ row, anchor: anchorFrom(event.currentTarget) })}>
        Record a result
      </Button>
    );
  }

  // Not gated on `canAct`: the hiring manager selects (ADMIN-15), and the server says who may.
  function selectCell(row: AssessmentResultRow): ReactNode {
    if (!selected.data) return null;
    if (selected.data.selectBlockedReason) return <span className={styles.meta}>{selected.data.selectBlockedReason}</span>;
    return (
      <Button variant="solid" size="sm" icon={Award} isPending={actions.pending.has(row.applicationId)} onClick={() => void actions.select(row)}>
        Select
      </Button>
    );
  }

  const columns: readonly Column<AssessmentResultRow>[] = [
    {
      id: 'candidate',
      header: 'Candidate',
      isIdentity: true,
      defaultWidth: 220,
      cell: (row) => (
        <span className={styles.who}>
          <Avatar name={row.candidateName} />
          <a href={`/candidates/${row.candidateId}`} className={styles.whoName} onClick={(event) => openPopup(event, row)}>
            {row.candidateName}
          </a>
        </span>
      ),
    },
    {
      id: 'score',
      header: 'Score',
      isNumeric: true,
      defaultWidth: 150,
      cell: (row) =>
        row.latest?.scorePercent === null || row.latest?.scorePercent === undefined ? null : (
          <span className={styles.score}>
            <strong>{row.latest.scorePercent}%</strong>
            {row.latest.didPass === true && <Chip tone={RESULT_TONES.passed} plain>Passed</Chip>}
            {row.latest.didPass === false && <Chip tone={RESULT_TONES.below} plain>Below</Chip>}
            {row.latest.hasPendingManualGrading && <Chip tone={RESULT_TONES.marking} plain>Marking</Chip>}
          </span>
        ),
    },
    {
      id: 'status',
      header: 'Test',
      defaultWidth: 170,
      cell: (row) => (
        <span className={styles.questionCell}>
          <span>{attemptLabel(row)}</span>
          {row.latest && (
            <span className={styles.meta}>
              {row.latest.examSession
                ? `${row.latest.examSession.name}${row.latest.examSession.startsAt ? ` · ${formatDateTime(row.latest.examSession.startsAt)}` : ''}`
                : row.latest.recorded
                  ? `${row.latest.testName} · ${row.latest.scoreRaw ?? '—'}/${row.latest.scoreMax ?? '—'}`
                  : `Attempt ${row.latest.attemptNumber}`}
              {row.attemptsAllowed > 1 ? ` · ${row.attemptsUsed} of ${row.attemptsAllowed} used` : ''}
              {row.missedSessions > 0 ? ` · missed ${row.missedSessions}` : ''}
            </span>
          )}
        </span>
      ),
    },
    {
      id: 'sections',
      header: 'Sections',
      defaultWidth: 200,
      cell: (row) =>
        row.latest && row.latest.sections.length > 1 && row.latest.scoreRaw !== null
          ? row.latest.sections.map((section) => `${section.name} ${section.scoreRaw ?? '—'}/${section.scoreMax ?? '—'}`).join(' · ')
          : null,
    },
    { id: 'stage', header: 'Stage', defaultWidth: 130, cell: (row) => stageLabels[row.stage] },
    {
      id: 'next',
      header: 'Next step',
      defaultWidth: 220,
      cell: (row) => {
        if (row.stage !== 'assessment') return null;
        if (isFinalStep && row.latest?.didPass === true) return selectCell(row);
        if (!canAct) return null;
        if (row.latest?.didPass === true) {
          return (
            <Button variant="solid" size="sm" icon={FastForward} onClick={() => advance([row])}>
              Move to {nextRound}
            </Button>
          );
        }
        if (row.latest?.didPass === false) {
          return (
            <Button variant="outline" size="sm" icon={XCircle} onClick={(event: MouseEvent<HTMLButtonElement>) => reject([row], event)}>
              Reject…
            </Button>
          );
        }
        if (view.isByHand && row.canRecord) return recordButton(row, 'solid');
        return null;
      },
    },
    {
      id: 'time',
      header: 'Taken',
      defaultWidth: 150,
      cell: (row) => {
        if (row.latest?.recorded) return formatDate(row.latest.recorded.takenOn);
        return row.latest?.submittedAt ? <span title={formatDateTime(row.latest.submittedAt)}>{formatRelative(row.latest.submittedAt)}{row.latest.minutesTaken !== null ? ` · ${row.latest.minutesTaken} min` : ''}</span> : null;
      },
    },
    {
      id: 'expires',
      header: 'Link',
      defaultWidth: 170,
      cell: (row) => (row.latest?.isLinkOpen && row.latest.expiresAt ? <span title={formatDateTime(row.latest.expiresAt)}>Expires {formatRelative(row.latest.expiresAt)}</span> : null),
    },
  ];

  return (
    <div className={styles.page}>
      <Card
        title={view.isByHand ? 'Test taken outside the app' : view.test ? view.test.name : 'No test chosen'}
        meta={
          view.isByHand
            ? `Each result is typed in · Pass mark ${view.passPercent}% · ${view.maxAttempts} attempt${view.maxAttempts === 1 ? '' : 's'}`
            : view.test
            ? `${view.isOffline ? `On paper · ${view.noShowChances} second chance${view.noShowChances === 1 ? '' : 's'} after a no-show · ` : ''}Pass mark ${view.passPercent}% · ${view.maxAttempts} attempt${view.maxAttempts === 1 ? '' : 's'} · version ${view.test.versionNumber}`
            : 'Choose which test to send in the job setup, step 5'
        }
        action={
          <div className={styles.itemActions}>
            {view.isOffline && (
              <Button variant="ghost" size="sm" icon={CalendarPlus} onClick={() => void navigate({ to: '/assessments/sessions' })}>
                Exam sessions
              </Button>
            )}
            <Button variant="ghost" size="sm" icon={Settings2} onClick={() => void navigate({ to: '/jobs/$jobId/setup/$section', params: { jobId, section: 'stages' } })}>
              Test settings
            </Button>
          </div>
        }
      >
        <MetricStrip
          metrics={view.isByHand ? [
            { label: 'Waiting for a result', value: String(count((row) => row.stage === 'assessment' && row.latest === null)) },
            { label: 'Passed', value: String(count((row) => row.latest?.didPass === true)) },
            { label: 'Below pass', value: String(count((row) => row.latest?.didPass === false)) },
          ] : view.isOffline ? [
            { label: 'Booked', value: String(count((row) => row.latest?.status === 'not_started')) },
            { label: 'No-shows', value: String(count((row) => row.latest?.status === 'abandoned')) },
            { label: 'Not booked', value: String(count((row) => row.nextAction === 'book')) },
            { label: 'Passed', value: String(count((row) => row.latest?.didPass === true)) },
            { label: 'Below pass', value: String(count((row) => row.latest?.didPass === false)) },
          ] : [
            { label: 'Not started', value: String(count((row) => row.latest?.status === 'not_started')) },
            { label: 'In progress', value: String(count((row) => row.latest?.status === 'in_progress')) },
            toMarkMetric(count((row) => row.latest?.hasPendingManualGrading === true)),
            { label: 'Passed', value: String(count((row) => row.latest?.didPass === true)) },
            { label: 'Below pass', value: String(count((row) => row.latest?.didPass === false)) },
            { label: 'Expired', value: String(count((row) => row.latest?.status === 'expired')) },
          ]}
        />
      </Card>

      {canAct && ((passed.length > 0 && !isFinalStep) || below.length > 0) && (
        <div className={styles.actionsRow}>
          {passed.length > 0 && !isFinalStep && (
            <Button variant="solid" icon={FastForward} onClick={() => advance(passed)}>
              Move {passed.length} who passed to {nextRound}
            </Button>
          )}
          {below.length > 0 && (
            <Button variant="outline" icon={XCircle} onClick={(event: MouseEvent<HTMLButtonElement>) => reject(below, event)}>
              Reject {below.length} below the pass mark
            </Button>
          )}
        </div>
      )}

      <DataTable
        screenId="assessment-results"
        label="Test results"
        columns={columns}
        rows={view.rows}
        toneOf={resultTone}
        getRowId={(row) => row.applicationId}
        total={view.rows.length}
        sort={sort}
        onSortChange={setSort}
        selection={selection}
        onSelectionChange={setSelection}
        onOpenRow={(row) => void navigate({ to: '/applications/$applicationId', params: { applicationId: row.applicationId } })}
        rowActions={
          canAct
            ? (row) => (
                <>
                  {row.nextAction === 'book' && (
                    <Button variant="quiet" size="sm" icon={CalendarPlus} isPending={actions.pending.has(row.applicationId)} onClick={(event: MouseEvent<HTMLButtonElement>) => setBooking({ row, anchor: anchorFrom(event.currentTarget) })}>
                      {TEST_ACTION_LABELS.book}
                    </Button>
                  )}
                  {row.nextAction && row.nextAction !== 'book' && (
                    <Button variant="quiet" size="sm" icon={Send} isPending={actions.pending.has(row.applicationId)} onClick={() => void actions.sendTest(row)}>
                      {TEST_ACTION_LABELS[row.nextAction]}
                    </Button>
                  )}
                  {row.canRecord && !(view.isByHand && row.latest?.didPass !== false) && recordButton(row, 'quiet')}
                  {row.latest?.recorded && row.stage === 'assessment' && (
                    <Button variant="quiet" size="sm" icon={Pencil} onClick={(event: MouseEvent<HTMLButtonElement>) => setCorrecting({ row, anchor: anchorFrom(event.currentTarget) })}>
                      Correct score
                    </Button>
                  )}
                  {canCopyLink(row) && (
                    <Button variant="quiet" size="sm" icon={Copy} onClick={() => row.latest && void actions.copy(row.latest.id, row.candidateName)}>
                      Copy link
                    </Button>
                  )}
                </>
              )
            : undefined
        }
        bulkActions={
          canAct ? (
            <>
              {!isFinalStep && (
                <Button variant="quiet" size="sm" icon={FastForward} disabled={chosen.length === 0} onClick={() => advance(chosen)}>
                  Advance {chosen.length > 0 ? chosen.length : ''}
                </Button>
              )}
              <Button variant="quiet" size="sm" icon={XCircle} disabled={chosen.length === 0} onClick={(event: MouseEvent<HTMLButtonElement>) => reject(chosen, event)}>
                Reject {chosen.length > 0 ? chosen.length : ''}
              </Button>
            </>
          ) : undefined
        }
        emptyState={
          <EmptyState
            title={view.isByHand ? 'Nobody is waiting for a result yet' : view.isOffline ? 'Nobody is booked yet' : 'Nobody has been sent the test yet'}
            body={
              view.isByHand
                ? 'People moved to the Assessment stage appear here. In Contact, select the people who confirmed and use "Move to Assessment", then record each result here.'
                : view.isOffline
                ? 'People reaching the Assessment stage appear here. In Contact, select the people who confirmed and use "Add to exam session" — they move here, booked.'
                : view.test
                  ? 'People reaching the Assessment stage appear here. Send the test from the Pipeline tab, or here, once they arrive.'
                  : 'Choose which test to send first.'
            }
            action={
              view.isOffline || view.isByHand ? (
                <Button variant="outline" onClick={() => void navigate({ to: '/jobs/$jobId/$tab', params: { jobId, tab: 'contact' } })}>
                  Open Contact
                </Button>
              ) : (
                <Button variant="outline" onClick={() => void navigate({ to: '/jobs/$jobId/pipeline', params: { jobId } })}>
                  Open the pipeline
                </Button>
              )
            }
          />
        }
      />

      <Popover anchor={booking?.anchor ?? null} onClose={() => setBooking(null)} align="right" width={420} label="Book a session">
        {booking && (
          <BookSessionPanel
            jobId={jobId}
            who={booking.row.candidateName}
            needed={1}
            onCancel={() => setBooking(null)}
            onNewSession={() => {
              setBooking(null);
              setIsCreatingSession(true);
            }}
            onPick={(sessionId, sessionName) => {
              const row = booking.row;
              setBooking(null);
              void actions.book(row, sessionId, sessionName);
            }}
          />
        )}
      </Popover>
      <Popover anchor={recording?.anchor ?? null} onClose={() => setRecording(null)} align="right" width={380} label="Record a result">
        {recording && (
          <RecordResultPanel
            candidateName={recording.row.candidateName}
            testName={view.test?.name ?? ''}
            applicationVersion={recording.row.version}
            onCancel={() => setRecording(null)}
            onSave={(input, scoresheet) => saveRecorded(recording.row, input, scoresheet)}
          />
        )}
      </Popover>
      <Popover anchor={correcting?.anchor ?? null} onClose={() => setCorrecting(null)} align="right" width={320} label="Correct score">
        {correcting?.row.latest && (
          <CorrectResultPanel
            candidateName={correcting.row.candidateName}
            score={correcting.row.latest.scoreRaw ?? 0}
            outOf={correcting.row.latest.scoreMax ?? 1}
            attemptVersion={correcting.row.latest.version}
            onCancel={() => setCorrecting(null)}
            onSave={(input) => saveCorrected(correcting.row, input)}
          />
        )}
      </Popover>
      <SessionEditor isOpen={isCreatingSession} onClose={() => setIsCreatingSession(false)} session={null} jobId={jobId} />

      <Popover anchor={rejecting?.anchor ?? null} onClose={() => setRejecting(null)} align="right" label="Reject">
        {rejecting && (
          <ReasonPicker
            mode="reject"
            reasons={reasonOptions(options.data?.reasons ?? [], ['assessment'])}
            pools={options.data?.pools ?? []}
            canPool={false}
            contactAttempts={CONTACT_ATTEMPTS_CHECKED_BY_SERVER}
            candidateName={rejecting.rows.length === 1 ? (rejecting.rows[0]?.candidateName ?? '1 person') : `${rejecting.rows.length} people`}
            initialReasonCode={FAILED_TEST_REASON}
            canAddToCvBank={offersCvBank(rejecting.rows, mayAddToCvBank)}
            onConfirm={(choice) => {
              const rows = rejecting.rows;
              setRejecting(null);
              setSelection(EMPTY_SELECTION);
              void actions.runBulk(rejectRequestOf(rows, choice), rows, 'rejected');
            }}
          />
        )}
      </Popover>
      {openCandidate && (
        <CandidatePopup
          candidateId={openCandidate.candidateId}
          tab={openCandidate.tab}
          onTabChange={(tab) => setOpenCandidate({ ...openCandidate, tab })}
          onClose={() => setOpenCandidate(null)}
        />
      )}
    </div>
  );
}
